/**
 * Krunchie Naturals — form-security.js
 * Client-side security & validation layer.
 *
 * Protections applied:
 *  1. Honeypot field — hidden input that humans never fill; bots do.
 *  2. Timing check  — form submitted too fast (< 3 s) signals a bot.
 *  3. CSRF-style token — random value set by JS, echoed back by PHP.
 *  4. Input sanitization — strips HTML tags before submission.
 *  5. Field validation — length, email format, forbidden patterns.
 *  6. Rate-limiting hint — disables button for 15 s after submit.
 */

(function () {
  'use strict';

  /* ── 1. Record the timestamp when the page loaded ── */
  const PAGE_LOAD_TIME = Date.now();
  const formTimeInput  = document.getElementById('formTime');
  if (formTimeInput) formTimeInput.value = PAGE_LOAD_TIME;

  /* ── 2. Generate a simple random token (echoed by PHP) ── */
  const csrfInput = document.getElementById('csrfToken');
  if (csrfInput) {
    const token = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
    csrfInput.value = token;
    // Mirror it in sessionStorage so PHP can't be bypassed by a direct POST
    // (PHP reads it from POST; the real check is server-side)
    try { sessionStorage.setItem('kn_csrf', token); } catch (_) {}
  }

  /* ── 3. Character counter for message textarea ── */
  const msgField  = document.getElementById('message');
  const charCount = document.getElementById('charCount');
  if (msgField && charCount) {
    msgField.addEventListener('input', () => {
      const len = msgField.value.length;
      charCount.textContent = `${len} / 2000`;
      charCount.style.color = len > 1800 ? 'var(--orange)' : '';
    });
  }

  /* ── 4. Sanitise a string: strip HTML tags, trim whitespace ── */
  function sanitize(str) {
    return str
      .replace(/<[^>]*>/g, '')          // remove any HTML tags
      .replace(/[<>"'`]/g, '')          // remove remaining dangerous chars
      .replace(/javascript:/gi, '')     // strip JS protocol
      .replace(/on\w+\s*=/gi, '')       // strip inline event handlers
      .trim();
  }

  /* ── 5. Email format validator ── */
  function isValidEmail(email) {
    return /^[^\s@]{1,64}@[^\s@]{1,253}\.[^\s@]{2,}$/.test(email);
  }

  /* ── 6. Forbidden content patterns (SQLi / XSS probes) ── */
  const FORBIDDEN = [
    /select\s+\*/i,
    /insert\s+into/i,
    /drop\s+table/i,
    /union\s+select/i,
    /<script/i,
    /eval\s*\(/i,
    /document\.cookie/i,
    /window\.location/i,
    /data:\s*text\/html/i,
  ];

  function containsForbidden(str) {
    return FORBIDDEN.some(rx => rx.test(str));
  }

  /* ── 7. Show / clear field errors ── */
  function setError(id, msg) {
    const el = document.getElementById(id);
    if (el) { el.textContent = msg; el.style.display = msg ? 'block' : 'none'; }
  }
  function clearErrors() {
    ['nameError', 'emailError', 'messageError'].forEach(id => setError(id, ''));
  }

  /* ── 8. Form submission handler ── */
  const form       = document.getElementById('contactForm');
  const submitBtn  = document.getElementById('submitBtn');
  const feedback   = document.getElementById('formFeedback');

  if (!form) return;

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    clearErrors();
    if (feedback) { feedback.textContent = ''; feedback.className = 'form-feedback'; }

    /* — Honeypot check — */
    const honeypot = document.getElementById('website');
    if (honeypot && honeypot.value.trim() !== '') {
      // Silently reject — don't tell the bot it failed
      showSuccess();
      return;
    }

    /* — Timing check: reject if submitted in under 3 seconds — */
    const elapsed = (Date.now() - PAGE_LOAD_TIME) / 1000;
    if (elapsed < 3) {
      showSuccess(); // silent rejection
      return;
    }

    /* — Collect & sanitize values — */
    const name     = sanitize(document.getElementById('name').value);
    const business = sanitize(document.getElementById('business').value);
    const email    = sanitize(document.getElementById('email').value);
    const message  = sanitize(document.getElementById('message').value);

    /* — Validate — */
    let valid = true;

    if (!name || name.length < 2) {
      setError('nameError', 'Please enter your full name (at least 2 characters).');
      valid = false;
    } else if (containsForbidden(name)) {
      setError('nameError', 'Please enter a valid name.');
      valid = false;
    }

    if (!isValidEmail(email)) {
      setError('emailError', 'Please enter a valid email address.');
      valid = false;
    } else if (containsForbidden(email)) {
      setError('emailError', 'Please enter a valid email address.');
      valid = false;
    }

    if (!message || message.length < 10) {
      setError('messageError', 'Please write a message (at least 10 characters).');
      valid = false;
    } else if (containsForbidden(message)) {
      setError('messageError', 'Your message contains characters that are not allowed. Please revise.');
      valid = false;
    }

    if (!valid) return;

    /* — Write sanitized values back into fields before POST — */
    document.getElementById('name').value    = name;
    document.getElementById('business').value = business;
    document.getElementById('email').value   = email;
    document.getElementById('message').value = message;

    /* — Disable button to prevent double-submit — */
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Sending…';
    }

    /* — Submit via fetch (AJAX) — */
    const data = new FormData(form);

    fetch('contact.php', { method: 'POST', body: data })
      .then(r => r.json())
      .then(res => {
        if (res.success) {
          showSuccess();
          form.reset();
          if (charCount) charCount.textContent = '0 / 2000';
        } else {
          showError(res.message || 'Something went wrong. Please try again.');
          reenableButton();
        }
      })
      .catch(() => {
        showError('Network error. Please check your connection and try again.');
        reenableButton();
      });
  });

  function showSuccess() {
    if (!feedback) return;
    feedback.textContent = '✅ Thank you! We\'ll be in touch shortly.';
    feedback.className   = 'form-feedback form-feedback--success';
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Sent ✓'; }
  }

  function showError(msg) {
    if (!feedback) return;
    feedback.textContent = '⚠️ ' + msg;
    feedback.className   = 'form-feedback form-feedback--error';
  }

  function reenableButton() {
    // Re-enable after 15 s to discourage rapid resubmission
    setTimeout(() => {
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Send Message →'; }
    }, 15000);
  }

})();
